import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { FaroSwitch } from "@components/common/faro-switch";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { ContactSupportLocations } from "@custom-types/types";
import { Alert, blue, neutral } from "@flat-ui";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Box, Typography } from "@mui/material";
import {
  FeatureSettingLayout,
  TOOLTIP_TEXT_CHECKED,
  TOOLTIP_TEXT_NOT_CHECKED,
} from "@pages/project-details/project-settings/feature-setting-layout";
import { useCompanyId } from "@context-providers/company-id-provider";
import { runtimeConfig } from "@src/runtime-config";
import {
  getProjectByIdSelector,
  isFeatureEnabledForProjectSelector,
  isSelectedProjectEditableSelector,
  selectedProjectContextSelector,
} from "@store/projects/projects-selector";
import { editProjectFeature } from "@store/projects/projects-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { hasUserValidFeatureProjectLevel } from "@utils/feature-control/project/project-feature-control";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import { isProjectDemo } from "@utils/project-utils";
import {
  ProjectEvents,
  WorkspaceEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Renders Face Blurring feature setting */
export function FeatureFaceBlurring({
  projectId,
}: BaseProjectIdProps): JSX.Element | null {
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);
  const selectedProject = useAppSelector(getProjectByIdSelector(projectId));
  const projectContext = useAppSelector(selectedProjectContextSelector);
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const { createDialog } = useSphereDialog();
  const { trackEvent } = useTrackEvent();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** Determines if the global face blurring feature is enabled for the selected project */
  const isFaceBlurringEnabled = useAppSelector(
    isFeatureEnabledForProjectSelector(projectId, "global-face-blurring")
  );

  const { canEditProjectSettings } = useHasUserValidPermissionProjectLevel({
    project: selectedProject,
  });

  /** Return true with showing a warning if face blurring v1 is available */
  async function onExistFaceBlurringV1(): Promise<boolean> {
    const hasFaceBlurringV1 = hasUserValidFeatureProjectLevel({
      roleName: RequiredFeatureProjectLevelName.canViewFaceBlurringV1Feature,
      featuresAvailable: projectContext?.featuresAvailable,
    });

    // Show warning dialog if face blurring v1 feature is available
    if (hasFaceBlurringV1) {
      const shouldContactSupport = await createDialog(
        {
          title: "Activating FaceBlurring is temporarily unavailable",
          confirmText: "Contact Support",
          cancelText: "Understood",
          size: "s",
        },
        <Box>
          <Alert
            title="FaceBlurring is still active, all images on already activated projects are still being blurred.
            We are preparing the rollout of a new and much improved automatic Face Blurring version."
            variant="error"
            sx={{
              fontSize: "14px",
              marginBottom: SPACE_ELEMENTS_OF_MODAL,
              backgroundColor: blue[100],
              color: neutral[1000],
            }}
          />
          <Typography
            sx={{
              marginBottom: SPACE_ELEMENTS_OF_MODAL,
              fontSize: "14px",
              color: neutral[800],
            }}
          >
            If you need to activate Face Blurring on a new project, just click
            “Contact Support” and we will help you swiftly.
          </Typography>
        </Box>
      );

      if (shouldContactSupport) {
        trackEvent({
          name: WorkspaceEvents.contactSupport,
          props: { location: ContactSupportLocations.faceBlurringV1Warning },
        });

        const subject = encodeURIComponent("FaceBlurring support needed");

        const body = encodeURIComponent(
          `Dear HoloBuilder team,\n\nI tried to activate FaceBlurring for my project ${projectId} in my company ${companyName}. \n\n It seems the FaceBlurring button is currently unavailable. Can you please provide support and activate the feature for this project?\n`
        );

        window.open(
          `mailto:${runtimeConfig.mailSupportAddress}?subject=${subject}&body=${body}`
        );
      }

      return true;
    }

    return false;
  }

  /** Triggers when feature settings toggle button in clicked */
  async function onChangeFeatureSetting(
    shouldChangeFeature: boolean
  ): Promise<void> {
    if (await onExistFaceBlurringV1()) {
      return;
    }

    const hasConfirmed = await createDialog(
      {
        title: "Activate Face Blurring",
        confirmText: "Activate",
        isHideClose: true,
      },
      <Box
        sx={{
          fontSize: "14px",
          color: neutral[800],
        }}
      >
        <Typography
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          Face Blurring cannot be deactivated. All faces in the project will be
          blurred permanently.
        </Typography>
        <Typography
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          Do you want to continue?
        </Typography>
      </Box>
    );

    if (!hasConfirmed) {
      return;
    }

    // Track event feature toggling
    trackEvent({
      name: ProjectEvents.adjustFeatureSettings,
      props: {
        featureName: "global-face-blurring",
        isFeatureEnabled: shouldChangeFeature,
      },
    });

    if (selectedProject) {
      setIsLoading(true);

      try {
        await dispatch(
          editProjectFeature({
            companyId,
            selectedProject,
            payload: {
              ["global-face-blurring"]: shouldChangeFeature,
            },
          })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `editFaceBlurringSetting-${Date.now().toString()}`,
          title: "Error changing face blurring setting",
          error,
        });
      } finally {
        setIsLoading(false);
      }
    }
  }

  // Early return if selected project is not available
  if (!selectedProject) {
    return null;
  }

  const actionComponent = (
    <FaroSwitch
      disabled={
        !isProjectEditable ||
        isProjectDemo(selectedProject) ||
        isFaceBlurringEnabled ||
        !canEditProjectSettings
      }
      checked={isFaceBlurringEnabled}
      isLoading={isLoading}
      onChange={(_, isChecked) => onChangeFeatureSetting(isChecked)}
      tooltipTextNotChecked={TOOLTIP_TEXT_NOT_CHECKED}
      tooltipTextChecked={TOOLTIP_TEXT_CHECKED}
      sx={{ cursor: !isProjectEditable ? "not-allowed" : "pointer" }}
    />
  );

  return (
    <FeatureSettingLayout
      featureName="Automatic Face Blurring"
      featureDescription="Blurs all faces visible in the 360 images of this project."
      actionComponent={actionComponent}
    />
  );
}
